/**
 * Connector instance wizard page component.
 * Step-by-step wizard for creating connector instances (Zapier-style).
 */

// React Imports
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from 'lucide-react';
import { Button, Chip, FormField, Input, ShellHeader, Switch, Textarea } from '@coordio/ui';
import { FormSection, InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import { connectorService } from '../services/connectorService';
import { useNotificationStore } from '../store/notificationStore';

import { ConnectorTestPanel } from '../components/connectors/ConnectorTestPanel';
import { ConnectorConfigRenderer } from '../components/connectors/ConnectorConfigRenderer';
import { CapabilityList } from '../components/connectors/CapabilityList';
import type {
  ConnectorDetailDto,
  CreateConnectorInstanceRequest,
} from '../types/connector';

const steps = ['Basic Information', 'Authentication', 'Settings & Defaults', 'Capabilities', 'Review & Create'];

/**
 * Connector instance wizard page component.
 */
export const ConnectorInstanceWizardPage = () => {
  const { connectorId } = useParams<{ connectorId: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [connector, setConnector] = useState<ConnectorDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  // Form data
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState<number | ''>('');
  const [rateLimitPerHour, setRateLimitPerHour] = useState<number | ''>('');
  const [rateLimitPerDay, setRateLimitPerDay] = useState<number | ''>('');
  const [credentialJson, setCredentialJson] = useState('');
  const [settingsJson, setSettingsJson] = useState('{}');
  const [enabledCapabilityIds, setEnabledCapabilityIds] = useState<string[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (connectorId) {
      loadConnector();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectorId]);

  const loadConnector = async () => {
    if (!connectorId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await connectorService.getConnector(connectorId);
      setConnector(data);
      setEnabledCapabilityIds(data.capabilities.filter((c) => c.isActive).map((c) => c.id));
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load connector';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!name.trim()) {
        setError('Instance name is required');
        return;
      }
    }
    if (activeStep === 1) {
      if (connector?.authenticationType === 'OAuth2' && !isConnected) {
        setError('Please connect using OAuth before proceeding');
        return;
      }
    }

    setError(null);
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleToggleCapability = (capabilityId: string, enabled: boolean) => {
    if (enabled) {
      setEnabledCapabilityIds([...enabledCapabilityIds, capabilityId]);
    } else {
      setEnabledCapabilityIds(enabledCapabilityIds.filter((id) => id !== capabilityId));
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    try {
      const request: CreateConnectorInstanceRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        isActive,
        rateLimitPerMinute: rateLimitPerMinute ? Number(rateLimitPerMinute) : undefined,
        rateLimitPerHour: rateLimitPerHour ? Number(rateLimitPerHour) : undefined,
        rateLimitPerDay: rateLimitPerDay ? Number(rateLimitPerDay) : undefined,
        credentialJson: credentialJson || undefined,
        settingsJson: settingsJson || '{}',
      };

      const instance = await connectorService.createConnectorInstance(connectorId!, request);
      addNotification('Connector instance created successfully', 'success');
      navigate(`/connectors/instances/${instance.id}`);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to create connector instance';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="connector-instance-wizard">
        <ShellHeader title="Create Connector Instance" description="Loading…" data-testid="connector-wizard-header" />
        <PageContainer data-testid="connector-wizard-form-page">
          <div className="flex justify-center p-12">
            <LoadingSkeleton rows={3} />
          </div>
        </PageContainer>
      </div>
    );
  }

  if (!connector) {
    return (
      <div className="connector-instance-wizard">
        <ShellHeader
          title="Create Connector Instance"
          description="Connector not found."
          data-testid="connector-wizard-header"
          actions={
            <Button type="button" variant="outline" size="sm" onClick={() => navigate('/connectors')} data-testid="back-connector-wizard-btn">
              Back
            </Button>
          }
        />
        <PageContainer data-testid="connector-wizard-form-page">
          <InlineErrorPanel message={error || 'Connector not found'} />
        </PageContainer>
      </div>
    );
  }

  const progressPct = ((activeStep + 1) / steps.length) * 100;

  return (
    <div className="connector-instance-wizard">
      <ShellHeader
        title={`Create ${connector.name} Instance`}
        description={`Configure a new instance of ${connector.name}`}
        data-testid="connector-wizard-header"
        actions={
          <Button type="button" variant="outline" size="sm" onClick={() => navigate(`/connectors/${connectorId}`)} data-testid="back-connector-wizard-btn">
            Back
          </Button>
        }
      />

      <PageContainer data-testid="connector-wizard-form-page">
      {/* Progress Stepper — kept as MUI (complex, no @coordio/ui equivalent) */}
      <div className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4 mb-4">
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <div className="mt-4 h-1 w-full rounded bg-[var(--color-bg-subtle)]">
          <div
            className="h-1 rounded bg-[var(--color-accent-primary)] transition-all duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6">
          <InlineErrorPanel message={error} action={<Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>} />
        </div>
      )}

      {/* Step Content */}
      <div className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-6">
          {/* Step 1: Basic Information */}
          {activeStep === 0 && (
            <FormSection title="Basic Information" description="Instance identity and status.">
              <div className="flex flex-col gap-5">
                <FormField label="Instance Name" required description="A unique name to identify this connector instance." data-testid="connector-wizard-name">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="connector-wizard-name"
                  />
                </FormField>
                <FormField label="Description" description="Optional description for this instance." data-testid="connector-wizard-description">
                  <Textarea
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    data-testid="connector-wizard-description"
                  />
                </FormField>
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch checked={isActive} onCheckedChange={setIsActive} data-testid="connector-wizard-isActive" />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            </FormSection>
          )}

          {/* Step 2: Authentication */}
          {activeStep === 1 && (
            <FormSection title="Authentication" description="Configure credentials for this connector.">
              <div className="flex flex-col gap-5">
                <div className="rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-inset,#fafafa)] px-3 py-2 text-sm">
                  Authentication Type: <strong>{connector.authenticationType}</strong>
                </div>

                {connector.authenticationType === 'OAuth2' && (
                  <div className="rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-inset,#fafafa)] px-3 py-2 text-sm">
                    OAuth connection will be configured after instance creation. You can connect in the instance detail page.
                  </div>
                )}

                {(connector.authenticationType === 'ApiKey' || connector.authenticationType === 'BearerToken') && (
                  <FormField
                    label={connector.authenticationType === 'ApiKey' ? 'API Key' : 'Bearer Token'}
                    required
                    description={`Enter your ${connector.authenticationType === 'ApiKey' ? 'API key' : 'bearer token'}`}
                    data-testid="connector-wizard-credential"
                  >
                    <Input
                      type="password"
                      value={credentialJson}
                      onChange={(e) => setCredentialJson(e.target.value)}
                      data-testid="connector-wizard-credential"
                    />
                  </FormField>
                )}

                {connector.authenticationType === 'BasicAuth' && (
                  <div className="grid gap-5 sm:grid-cols-2">
                    <FormField label="Username" required data-testid="connector-wizard-username">
                      <Input
                        value={credentialJson ? JSON.parse(credentialJson || '{}').username || '' : ''}
                        onChange={(e) => {
                          const current = credentialJson ? JSON.parse(credentialJson || '{}') : {};
                          setCredentialJson(JSON.stringify({ ...current, username: e.target.value }));
                        }}
                        data-testid="connector-wizard-username"
                      />
                    </FormField>
                    <FormField label="Password" required data-testid="connector-wizard-password">
                      <Input
                        type="password"
                        value={credentialJson ? JSON.parse(credentialJson || '{}').password || '' : ''}
                        onChange={(e) => {
                          const current = credentialJson ? JSON.parse(credentialJson || '{}') : {};
                          setCredentialJson(JSON.stringify({ ...current, password: e.target.value }));
                        }}
                        data-testid="connector-wizard-password"
                      />
                    </FormField>
                  </div>
                )}

                <ConnectorTestPanel
                  instanceId={connectorId!}
                  connectorName={connector.name}
                  onTestComplete={(result) => {
                    if (result.success) {
                      setIsConnected(true);
                    }
                  }}
                />
              </div>
            </FormSection>
          )}

          {/* Step 3: Settings & Defaults */}
          {activeStep === 2 && (
            <div className="flex flex-col gap-5">
              {connector && (
                <ConnectorConfigRenderer
                  connector={connector}
                  instanceId={connectorId}
                  settingsJson={settingsJson}
                  isConnected={isConnected}
                  level="Instance"
                  onChange={setSettingsJson}
                  onConnected={() => setIsConnected(true)}
                />
              )}

              <FormSection title="Rate Limits" description="Leave empty to inherit from connector defaults.">
                <div className="grid gap-5 sm:grid-cols-3">
                  <FormField label="Per Minute" description="Requests allowed per minute." data-testid="connector-wizard-rateLimitPerMinute">
                    <Input
                      type="number"
                      value={rateLimitPerMinute}
                      onChange={(e) => setRateLimitPerMinute(e.target.value ? Number(e.target.value) : '')}
                      data-testid="connector-wizard-rateLimitPerMinute"
                    />
                  </FormField>
                  <FormField label="Per Hour" description="Requests allowed per hour." data-testid="connector-wizard-rateLimitPerHour">
                    <Input
                      type="number"
                      value={rateLimitPerHour}
                      onChange={(e) => setRateLimitPerHour(e.target.value ? Number(e.target.value) : '')}
                      data-testid="connector-wizard-rateLimitPerHour"
                    />
                  </FormField>
                  <FormField label="Per Day" description="Requests allowed per day." data-testid="connector-wizard-rateLimitPerDay">
                    <Input
                      type="number"
                      value={rateLimitPerDay}
                      onChange={(e) => setRateLimitPerDay(e.target.value ? Number(e.target.value) : '')}
                      data-testid="connector-wizard-rateLimitPerDay"
                    />
                  </FormField>
                </div>
              </FormSection>
            </div>
          )}

          {/* Step 4: Capability Selection */}
          {activeStep === 3 && (
            <FormSection title="Capability Selection" description="Select which capabilities to enable for this instance. Only enabled capabilities will be available as tools.">
              <CapabilityList
                capabilities={connector.capabilities}
                enabledCapabilityIds={enabledCapabilityIds}
                onToggle={handleToggleCapability}
                showDetails
              />
            </FormSection>
          )}

          {/* Step 5: Review & Create */}
          {activeStep === 4 && (
            <FormSection title="Review & Create" description="Verify your configuration before creating the instance.">
              <div className="flex flex-col gap-2">
                <div className="flex justify-between">
                  <span className="text-sm text-[var(--color-text-secondary)]">Instance Name:</span>
                  <span className="text-sm font-medium">{name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[var(--color-text-secondary)]">Status:</span>
                  <Chip className="text-xs" color={isActive ? 'green' : 'default'} label={isActive ? 'Active' : 'Inactive'} />
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-[var(--color-text-secondary)]">Enabled Capabilities:</span>
                  <span className="text-sm font-medium">{enabledCapabilityIds.length} / {connector.capabilities.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[var(--color-text-secondary)]">Authentication:</span>
                  <Chip className="text-xs" color={isConnected ? 'green' : 'default'} label={isConnected ? 'Connected' : 'Not Connected'} />
                </div>
              </div>
            </FormSection>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={activeStep === 0}
              onClick={handleBack}
              data-testid="connector-wizard-back-btn"
            >
              Back
            </Button>
            <div className="flex gap-4">
              {activeStep < steps.length - 1 ? (
                <Button type="button" variant="default" size="sm" onClick={handleNext} data-testid="connector-wizard-next-btn">
                  Next
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className="gap-2"
                  onClick={handleCreate}
                  disabled={saving || !name.trim()}
                  data-testid="save-connector-wizard-btn"
                >
                  {saving ? (
                    'Creating...'
                  ) : (
                    <>
                      <CheckCircleIcon size={16} />
                      Create Instance
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </PageContainer>
    </div>
  );
};
